import type { CashPaymentStartResponseDTO, PaymentStatusDTO } from './checkoutApi.js';

function normalizeOrderNumberCandidate(
  value: string | number | null | undefined,
): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }
    return String(value);
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Reject recovery/order refs that are clearly the internal transaction id
 * (S2-01 / G1 — never show raw transaction.id as the customer order code).
 */
function isInternalTransactionIdRef(
  candidate: string,
  transactionId: number | null | undefined,
): boolean {
  if (transactionId == null || !Number.isFinite(transactionId) || transactionId <= 0) {
    return false;
  }
  return candidate === String(transactionId);
}

/**
 * Customer-facing order reference for the cash waiting screen (G6 / S2-01 / G3).
 * Prefer poll VS → start-cash VS → poll/start allocated → top-level orderReference
 * → recovery orderReference (non-internal). Never transactionId / session public ids.
 */
export function resolveCashCollectOrderNumber(
  cash: CashPaymentStartResponseDTO,
  paymentStatus?: PaymentStatusDTO | null,
): string | null {
  const variableSymbol =
    normalizeOrderNumberCandidate(paymentStatus?.variableSymbol) ??
    normalizeOrderNumberCandidate(cash.variableSymbol);
  if (variableSymbol !== null) {
    return variableSymbol;
  }

  const transactionId = paymentStatus?.transactionId ?? cash.transactionId;

  const allocatedDisplayNumber =
    normalizeOrderNumberCandidate(paymentStatus?.allocatedOrderDisplayNumber) ??
    normalizeOrderNumberCandidate(cash.allocatedOrderDisplayNumber);
  if (
    allocatedDisplayNumber !== null &&
    !isInternalTransactionIdRef(allocatedDisplayNumber, transactionId)
  ) {
    return allocatedDisplayNumber;
  }

  const topLevelOrderReference = normalizeOrderNumberCandidate(paymentStatus?.orderReference);
  if (
    topLevelOrderReference !== null &&
    !isInternalTransactionIdRef(topLevelOrderReference, transactionId)
  ) {
    return topLevelOrderReference;
  }

  const recoveryOrderReference = normalizeOrderNumberCandidate(
    paymentStatus?.receiptRecovery?.orderReference,
  );
  if (
    recoveryOrderReference !== null &&
    !isInternalTransactionIdRef(recoveryOrderReference, transactionId) &&
    !isInternalTransactionIdRef(
      recoveryOrderReference,
      paymentStatus?.receiptRecovery?.transactionId,
    )
  ) {
    return recoveryOrderReference;
  }

  return null;
}
